// Columna de filtros de Clientes, en el orden del mock: Búsqueda · Tipo de cliente · Tienda ·
// Vendedor · Nivel SK · Rango de compras · Última compra.
import type { ReactNode } from 'react';
import { Info, X } from 'lucide-react';
import { ClientLevelEnum } from '@fiado/type-kit/bin/loanScoring/enums/ClientLevelEnum';
import { Input, Select } from '@/kit';
import { cn } from '@/lib/cn';
import { FiltersColumn } from '@/shared/patterns/FiltersColumn';
import { TierPill } from '@/shared/patterns/Pills';
import {
    countActiveFilters,
    CUSTOMER_TYPE_FILTERS,
    CUSTOMER_TYPE_LABEL,
    DATE_PRESET_LABEL,
    DATE_PRESETS,
    EMPTY_FILTERS,
    LEVEL_FILTERS,
    toggleLevel,
    type CreditReadState,
    type CustomersFilters,
    type LevelFilter,
    type Option,
} from '../lib/customersList';
import { AGGREGATES_NO_SOURCE, LEVEL_ERROR, LEVEL_FORBIDDEN } from '../lib/customersFormat';

/** Radix no admite `value=""` en un item: «Todas» viaja con este centinela. */
const ALL = 'ALL';

const LABEL =
    'mb-[5px] flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground';
const FIELD = 'h-[26px] text-[11.5px]';

const LEVEL_VALUE: Record<LevelFilter, ClientLevelEnum | null> = {
    GOLD: ClientLevelEnum.GOLD,
    SILVER: ClientLevelEnum.SILVER,
    BRONZE: ClientLevelEnum.BRONZE,
    NONE: null,
};

const LEVEL_HINT =
    'Nivel asignado por SureKeep. Sirve como referencia para saber si el cliente ya es sujeto de crédito.';

function Group({
    label,
    hint,
    disabledReason,
    children,
}: {
    label: string;
    hint?: string;
    /** Con motivo, el grupo se ve apagado y el motivo va en su `title`. */
    disabledReason?: string;
    children: ReactNode;
}) {
    return (
        <div
            className={cn('mt-2.5 first:mt-0', disabledReason && 'opacity-50')}
            title={disabledReason}
        >
            <div className={LABEL}>
                {label}
                {hint ? (
                    <span title={hint} className="cursor-help font-normal">
                        <Info className="size-2.5" aria-label={hint} />
                    </span>
                ) : null}
            </div>
            {children}
        </div>
    );
}

function levelDisabledReason(state: CreditReadState): string | undefined {
    if (state === 'forbidden') return LEVEL_FORBIDDEN;
    if (state === 'error') return LEVEL_ERROR;
    return undefined;
}

export function CustomersFiltersPanel({
    filters,
    onChange,
    stores,
    sellers,
    creditState,
    hasAggregates,
    loadedNote,
    collapsed,
    onToggleCollapsed,
}: {
    filters: CustomersFilters;
    onChange: (next: CustomersFilters) => void;
    stores: Option[];
    /** Vendedores de la tienda elegida (o de todas). */
    sellers: Option[];
    creditState: CreditReadState;
    /** La lista trae el número de compras. Sin él, el rango no puede filtrar. */
    hasAggregates: boolean;
    /** Aviso de que la búsqueda y los filtros locales solo miran lo cargado. */
    loadedNote: string | null;
    collapsed: boolean;
    onToggleCollapsed: () => void;
}) {
    const patch = (partial: Partial<CustomersFilters>) => {
        onChange({ ...filters, ...partial });
    };
    const levelReason = levelDisabledReason(creditState);
    const levelOff = levelReason !== undefined || creditState === 'loading';
    const rangeReason = hasAggregates ? undefined : AGGREGATES_NO_SOURCE;

    return (
        <FiltersColumn
            collapsed={collapsed}
            onToggleCollapsed={onToggleCollapsed}
            activeCount={countActiveFilters(filters)}
            onReset={() => {
                onChange({ ...EMPTY_FILTERS, bucket: filters.bucket });
            }}
        >
            {loadedNote === null ? null : (
                <div
                    role="status"
                    className="mb-2 rounded border border-border bg-muted/40 px-2 py-1.5 text-[10px] leading-snug text-muted-foreground"
                >
                    {loadedNote}
                </div>
            )}

            <Group label="Búsqueda">
                <div className="relative">
                    <Input
                        type="text"
                        value={filters.query}
                        onChange={(e) => {
                            patch({ query: e.target.value });
                        }}
                        placeholder="Nombre, tel, ID"
                        aria-label="Buscar cliente por nombre, teléfono o ID"
                        className={cn(FIELD, 'pr-6')}
                    />
                    {filters.query ? (
                        <button
                            type="button"
                            onClick={() => {
                                patch({ query: '' });
                            }}
                            title="Limpiar"
                            aria-label="Limpiar búsqueda"
                            className="absolute right-1 top-1/2 flex size-4 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
                        >
                            <X className="size-3" aria-hidden />
                        </button>
                    ) : null}
                </div>
            </Group>

            <Group
                label="Tipo de cliente"
                hint="Solo cuenta: abrió su cuenta PCF y todavía no compra."
            >
                <Select
                    value={filters.customerType}
                    onChange={(value) => {
                        const customerType = CUSTOMER_TYPE_FILTERS.find((t) => t === value);
                        if (customerType) patch({ customerType });
                    }}
                    options={CUSTOMER_TYPE_FILTERS.map((type) => ({
                        value: type,
                        label: CUSTOMER_TYPE_LABEL[type],
                    }))}
                    className={FIELD}
                />
            </Group>

            <Group label="Tienda" hint="Clientes que compraron en esta tienda.">
                <Select
                    value={filters.storeId || ALL}
                    onChange={(value) => {
                        const storeId = value === ALL ? '' : value;
                        patch({ storeId, sellerId: '' });
                    }}
                    options={[
                        { value: ALL, label: `Todas (${String(stores.length)})` },
                        ...stores.map((s) => ({ value: s.id, label: s.name })),
                    ]}
                    className={FIELD}
                />
            </Group>

            <Group label="Vendedor" hint="Clientes atendidos por este vendedor.">
                <Select
                    value={filters.sellerId || ALL}
                    onChange={(value) => {
                        patch({ sellerId: value === ALL ? '' : value });
                    }}
                    options={[
                        { value: ALL, label: 'Todos' },
                        ...sellers.map((s) => ({ value: s.id, label: s.name })),
                    ]}
                    className={FIELD}
                />
            </Group>

            <Group label="Nivel SK" hint={LEVEL_HINT} disabledReason={levelReason}>
                <div className="flex flex-wrap gap-[3px]">
                    {LEVEL_FILTERS.map((level) => (
                        <TierPill
                            key={level}
                            level={LEVEL_VALUE[level]}
                            selected={filters.levels.includes(level)}
                            disabled={levelOff}
                            title={levelReason}
                            onToggle={() => {
                                patch({ levels: toggleLevel(filters.levels, level) });
                            }}
                        />
                    ))}
                </div>
            </Group>

            <Group label="Rango de compras (#)" disabledReason={rangeReason}>
                <div className="flex items-center gap-1">
                    <Input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        disabled={!hasAggregates}
                        value={filters.purchasesMin}
                        onChange={(e) => {
                            patch({ purchasesMin: e.target.value });
                        }}
                        placeholder="mín"
                        aria-label="Compras mínimas"
                        className={cn(FIELD, 'tabular-nums')}
                    />
                    <span className="text-[11px] text-muted-foreground">—</span>
                    <Input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        disabled={!hasAggregates}
                        value={filters.purchasesMax}
                        onChange={(e) => {
                            patch({ purchasesMax: e.target.value });
                        }}
                        placeholder="máx"
                        aria-label="Compras máximas"
                        className={cn(FIELD, 'tabular-nums')}
                    />
                </div>
            </Group>

            <Group
                label="Última compra"
                hint="A quien solo tiene cuenta lo filtra por la fecha en que la abrió."
            >
                <Select
                    value={filters.datePreset}
                    onChange={(value) => {
                        const preset = DATE_PRESETS.find((p) => p === value);
                        if (preset) patch({ datePreset: preset });
                    }}
                    options={DATE_PRESETS.map((preset) => ({
                        value: preset,
                        label: DATE_PRESET_LABEL[preset],
                    }))}
                    className={FIELD}
                />
                {filters.datePreset === 'CUSTOM' ? (
                    <div className="mt-1 flex items-center gap-1">
                        <Input
                            type="date"
                            value={filters.from}
                            onChange={(e) => {
                                patch({ from: e.target.value });
                            }}
                            aria-label="Última compra desde"
                            className={cn(FIELD, 'min-w-0 px-1')}
                        />
                        <span className="text-[11px] text-muted-foreground">—</span>
                        <Input
                            type="date"
                            value={filters.to}
                            onChange={(e) => {
                                patch({ to: e.target.value });
                            }}
                            aria-label="Última compra hasta"
                            className={cn(FIELD, 'min-w-0 px-1')}
                        />
                    </div>
                ) : null}
            </Group>
        </FiltersColumn>
    );
}
